import { DOCUMENT, Injectable, computed, inject, signal } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { EMPTY, Observable, Subscription, fromEvent, merge, timer } from "rxjs";
import { catchError, filter, switchMap, tap } from "rxjs/operators";

import { environment } from "../../../environments/environment";

interface VersionFile {
  version: string;
}

const VERSION_FILE = "assets/version.json";
const POLL_INTERVAL_MS = 5 * 60 * 1000;
const MIN_CHECK_GAP_MS = 30 * 1000;

/**
 * The deployed build writes its commit sha both into environment.version and
 * into assets/version.json. Polling that file tells a long-lived tab that the
 * bundle it is running is no longer the one the server serves.
 */
@Injectable({ providedIn: "root" })
export class VersionCheckService {
  private http = inject(HttpClient);
  private document = inject<Document>(DOCUMENT);

  private readonly deployedVersion = signal<string | null>(null);
  private readonly dismissedVersion = signal<string | null>(null);

  public readonly updateAvailable = computed(() => {
    const deployed = this.deployedVersion();

    return (
      deployed !== null &&
      deployed !== environment.version &&
      deployed !== this.dismissedVersion()
    );
  });

  private subscription: Subscription;
  private lastCheckAt = 0;

  public start(): void {
    if (!environment.production || this.subscription) {
      return;
    }

    const window = this.document.defaultView;

    this.subscription = merge(
      timer(0, POLL_INTERVAL_MS),
      fromEvent(window, "focus"),
      fromEvent(this.document, "visibilitychange"),
    )
      .pipe(
        filter(() => !this.document.hidden),
        filter(() => Date.now() - this.lastCheckAt >= MIN_CHECK_GAP_MS),
        tap(() => (this.lastCheckAt = Date.now())),
        switchMap(() => this.fetchDeployedVersion()),
      )
      .subscribe((file) => this.deployedVersion.set(file.version));
  }

  public stop(): void {
    this.subscription?.unsubscribe();
    this.subscription = undefined;
  }

  public dismiss(): void {
    this.dismissedVersion.set(this.deployedVersion());
  }

  public reload(): void {
    this.document.defaultView.location.reload();
  }

  private fetchDeployedVersion(): Observable<VersionFile> {
    return this.http
      .get<VersionFile>(`${VERSION_FILE}?t=${Date.now()}`, {
        headers: { "Cache-Control": "no-cache" },
      })
      .pipe(catchError(() => EMPTY));
  }
}
